<template>
  <div class="input-text-wrapper" :class="{
    'has-warning': !hasValidation,
    'has-success': hasValidation,
    'is-disabled': isDisabled
  }">
    <!-- Label -->
    <label v-if="label && label !== ''" :for="elemId" class="input-label">
      <i v-if="isRequired && !hasValidation" class="pi pi-exclamation-triangle text-warning"></i>
      <i v-if="isRequired && hasValidation" class="pi pi-check text-success"></i>
      <span v-if="isRequired" class="required-asterisk">*</span>
      <span>{{ label }}</span>
    </label>

    <!-- PrimeVue Textarea -->
    <Textarea
      v-model="internalValue"
      :id="elemId"
      :placeholder="placeholder"
      :disabled="isDisabled"
      :invalid="isRequired && !hasValidation"
      :variant="variant"
      :rows="rows"
      :fluid="fluid"
      :data-testid="dataTestid || elemId"
      @update:modelValue="handleUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import Textarea from 'primevue/textarea';

const props = defineProps({
  // Legacy props (backward compatibility)
  elemId: String,
  placeholder: String,
  label: String,
  validation: {
    type: Function as any,
    default: () => true
  },
  required: {
    type: [String, Boolean],
    default: false
  },
  disabled: {
    type: [String, Boolean],
    default: false
  },
  modelValue: String,
  value: String, // Keep for backward compatibility
  dataTestid: String,

  // PrimeVue Textarea props
  rows: {
    type: Number,
    default: 3
  },
  variant: {
    type: String,
    default: 'outlined' // 'outlined' or 'filled'
  },
  fluid: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits<{
  'update:modelValue': [value: any];
  'input': [value: any];
}>();

// Reactive data
const internalValue = ref<string>(props.modelValue || props.value || '');

// Computed properties
const isRequired = computed((): boolean =>
  props.required === true || props.required === 'true'
);

const isDisabled = computed((): boolean =>
  props.disabled === true || props.disabled === 'true'
);

const hasValidation = computed((): boolean =>
  props.validation()
);

// Watchers
watch(() => props.modelValue, (newVal) => {
  internalValue.value = newVal || '';
});

watch(() => props.value, (newVal) => {
  internalValue.value = newVal || '';
});

// Handle update from PrimeVue Textarea
function handleUpdate(newVal: any): void {
  emit('update:modelValue', newVal);
  emit('input', newVal); // Keep for backward compatibility
}
</script>

<style scoped>
.input-text-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
  color: var(--p-text-color);
  white-space: nowrap;
}

.required-asterisk {
  color: var(--p-red-500);
  margin-right: 0.25rem;
}

.text-warning {
  color: var(--p-yellow-500);
}

.text-success {
  color: var(--p-green-500);
}

.has-warning :deep(.p-inputtext) {
  border-color: var(--p-yellow-500);
}

.has-success :deep(.p-inputtext) {
  border-color: var(--p-green-500);
}

.is-disabled {
  opacity: 0.5;
  pointer-events: none;
}
</style>
